import React, { useState } from "react";
import {
  Card,
  Box,
  Typography,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  makeStyles,
  Grid,
} from "@material-ui/core";
import { DataGrid } from "@material-ui/data-grid";
import { PowerSettingsNew, Edit, Launch } from "@material-ui/icons";
import SensorAssignActionBtn from "./ActionBtn";
import UpdateDisplayName from "./UpdateDisplayName";
import { useLocation } from "react-router-dom";
import smartParkService from "../../../axios";
import { useSelector } from "react-redux";
import { login_ } from "../../../reducers/login";
import _ from "lodash";
import { useHistory } from "react-router-dom";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";

const useStyles = makeStyles((theme) => ({
  card: {
    borderRadius: 12,
    overflow: "hidden",
    height: "100%",
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    padding: theme.spacing(3),
    background: "linear-gradient(180deg, #e8f4f8 0%, #d9eef3 100%)",
    borderBottom: "4px solid #0076bb",
  },
  title: {
    color: "#0076bb",
    fontWeight: 700,
    lineHeight: 1.2,
  },
  subtitle: {
    marginTop: theme.spacing(0.75),
  },
  chipContainer: {
    display: "flex",
    flexWrap: "wrap",
    marginTop: theme.spacing(2),
  },
  chip: {
    backgroundColor: "#fff",
    fontWeight: 600,
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  successChip: {
    color: "#2e7d32",
    borderColor: "#2e7d32",
  },
  errorChip: {
    color: "#d32f2f",
    borderColor: "#d32f2f",
  },
  primaryChip: {
    color: "#0076bb",
    borderColor: "#0076bb",
  },
  lastUpdatedContainer: {
    padding: theme.spacing(2, 3),
    backgroundColor: "#d9eef3",
    borderTop: "1px solid rgba(0,0,0,0.08)",
  },
  lastUpdated: {
    color: "#009444",
    fontWeight: 600,
  },
  content: {
    padding: theme.spacing(3),
    flex: 1,
  },
  sectionTitle: {
    color: "#0076bb",
    fontWeight: 700,
    fontSize: 12,
    letterSpacing: 1,
    textTransform: "uppercase",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(0.5, 1),
    borderRadius: 4,
    backgroundColor: "#fafafa",
  },
  detailLabel: {
    color: "#0076bb",
    fontWeight: 500,
  },
  copyButton: {
    color: "#0076bb",
    "&:hover": {
      backgroundColor: "#d9eef3",
    },
  },
  divider: {
    margin: theme.spacing(0.5, 0),
  },
}));

const MiniChart = ({ data = [] }) => {
  const seriesData = data.map((item) => [
    item.reportDateUNIX,
    Number(item.value),
  ]);
  const options = {
    chart: {
      type: "area",
      height: 50,
      backgroundColor: "transparent",
      margin: [1, 1, 1, 1],
      spacing: [0, 0, 0, 0],
    },

    title: {
      text: null,
    },

    credits: {
      enabled: false,
    },

    legend: {
      enabled: false,
    },

    xAxis: {
      type: "datetime",
      visible: false,
    },

    yAxis: {
      visible: false,
      title: {
        text: null,
      },
    },

    tooltip: {
      xDateFormat: "%b %e, %Y %H:%M:%S",
      pointFormat: "<b>{point.y}</b>",
    },

    plotOptions: {
      series: {
        animation: false,
        marker: {
          enabled: false,
        },
        enableMouseTracking: true,
      },
    },
    series: [
      {
        type: "area",
        data: seriesData,
        color: "#0076bb",
        lineColor: "#0076bb",
        lineWidth: 2,
        fillColor: {
          linearGradient: {
            x1: 0,
            y1: 0,
            x2: 0,
            y2: 1,
          },
          stops: [
            [0, "rgba(0,118,187,0.45)"],
            [1, "rgba(0,118,187,0.05)"],
          ],
        },
        marker: {
          enabled: false,
        },
        states: {
          hover: {
            lineWidth: 2,
          },
        },
      },
    ],
  };

  return (
    <div style={{ width: "100%", height: "50px" }}>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </div>
  );
};

export default function SensorInfoCard({
  sensors = [],
  refreshHandler,
  paginationData,
  paginationHandler,
}) {
  const history = useHistory();
  const loginDetails = useSelector(login_);
  const userid = _.get(loginDetails, ["UserID"], "");
  const classes = useStyles();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectionModel, setSelectionModel] = useState([]);
  const initialModalDetails = {
    title: null,
    label: null,
    action: null,
    open: false,
    row: null,
    value: null,
  };
  const handleSelectionChange = (selection) => {
    setSelectionModel(selection);
    const selectedRows = sensors.filter((row) => selection.includes(row.id));
    setSelectedRows(selectedRows);
  };
  const [modalDetails, setModalDetails] = useState(initialModalDetails);
  const saveButtonHandler = async () => {
    const params = new URLSearchParams(location.search);
    const sensorId = params.get("sensorId");
    const { action, value, row } = modalDetails;

    const idsArray =
      action === "bulkenable" || action === "bulkdisable"
        ? row.map((r) => r.id)
        : [row.id];
    const payload = {
      sensorID: sensorId,
      readingIds: idsArray,
      setIsActive:
        modalDetails.action === "enable" || modalDetails.action === "bulkenable"
          ? true
          : false,
    };
    const renamePayload = {
      readingID: row.id,
      displayName: value,
    };
    let url = `api/sensors/dot-sensor/${action === "rename" ? "updateReadingDisplayName" : "updateReadingStatus"}`;

    setLoading(true);

    try {
      const res = await smartParkService.post(
        url,
        action === "rename" ? renamePayload : payload,
        {
          headers: {
            "x-access-token": "citizen",
            userid: userid,
          },
        },
      );
      if (res.status === 200) {
        if (res?.data) {
          alert(res?.data?.message);
        }
        refreshHandler();
      }
    } catch (err) {
      console.error("Failed to update display name:", err);
    } finally {
      setLoading(false);
      setModalDetails(initialModalDetails);
      handleSelectionChange([]);
    }
  };
  const menuItemHandler = ({ option, row }) => {
    const attribute = row?.attribute_display_name || row?.attribute_name;

    if (option.value === "enable") {
      setModalDetails((prev) => ({
        ...prev,
        open: true,
        action: option.value,
        row: row,
        value: attribute,
        title: "Enable Attribute",
        label: "Attribute Name",
      }));
    }
    if (option.value === "disable") {
      setModalDetails((prev) => ({
        ...prev,
        open: true,
        action: option.value,
        row: row,
        value: attribute,
        title: "Disable Attribute",
        label: "Attribute Name",
      }));
    }
    if (option.value === "rename") {
      setModalDetails((prev) => ({
        ...prev,
        open: true,
        action: option.value,
        row: row,
        value: attribute,
        title: "Display Name",
        label: "Update Display Name",
      }));
    }
    if (option.value === "bulkenable") {
      setModalDetails((prev) => ({
        ...prev,
        open: true,
        action: option.value,
        row: row,
        value: attribute,
        title: "Display Name",
        label: "Update Display Name",
      }));
    }
    if (option.value === "bulkdisable") {
      setModalDetails((prev) => ({
        ...prev,
        open: true,
        action: option.value,
        row: row,
        value: attribute,
        title: "Display Name",
        label: "Update Display Name",
      }));
    }
  };
  const columns = [
    { field: "attribute_name", headerName: "Name", flex: 1 },
    {
      field: "attribute_display_name",
      headerName: "Display Name",
      flex: 1,
      valueGetter: (params) => params.value ?? "N.A",
    },
    {
      field: "type",
      headerName: "Type",
      flex: 1,
      valueGetter: (e) => {
        const row = e?.row;
        return (row?.context?.type).toUpperCase() ?? "-";
      },
    },
    {
      field: "count_object",
      headerName: "Count Object",
      flex: 1,
      valueGetter: (e) => {
        const row = e?.row;
        return (row?.context?.count_object).toUpperCase() ?? "-";
      },
    },
    { field: "last_value", headerName: "Value", flex: 1 },
    { field: "reportDateEST", headerName: "Last Updated", flex: 1 },
    {
      field: "graphData",
      headerName: "Graph",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return <MiniChart data={params.formattedValue} />;
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => {
        const row = params.row;
        const action = row.isActive ? "disable" : "enable";

        return (
          <Box>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                menuItemHandler({
                  option: { name: "Toggle", value: action },
                  row: row,
                });
              }}
            >
              <PowerSettingsNew
                style={{
                  color:
                    row.isActive === undefined
                      ? "#ff9800"
                      : row.isActive
                        ? "#2e7d32"
                        : "#d32f2f",
                }}
              />
            </IconButton>

            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                menuItemHandler({
                  option: { name: "Rename", value: "rename" },
                  row: row,
                });
              }}
            >
              <Edit />
            </IconButton>
            <IconButton
              onClick={(e) => {
                const params = new URLSearchParams(location.search);
                const sensorId = params.get("sensorId");
                const attributeId = row.id;
                e.stopPropagation();
                history.push(
                  `/AttributeDetails?sensorId=${sensorId}&attributeId=${attributeId}`,
                );
              }}
            >
              <Launch />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  return (
    <Card elevation={6} className={classes.card}>
      <Box className={classes.header}>
        <Box>
          <Typography variant="h5" className={classes.title}>
            Sensor Information
          </Typography>

          <Typography
            variant="h6"
            color="textSecondary"
            className={classes.subtitle}
          ></Typography>
        </Box>
      </Box>

      <Box className={classes.lastUpdatedContainer}>
        <Grid container>
          <Grid item xs={6}></Grid>
          <Grid item xs={6}>
            <SensorAssignActionBtn
              labels={[
                { name: "Enable", action: "bulkenable", color: "primary" },
                {
                  name: "Disable",
                  action: "bulkdisable",
                  color: "default",
                },
              ]}
              selectedData={selectedRows}
              buttonHandler={(e) => {
                menuItemHandler({ option: e, row: selectedRows });
              }}
            />
          </Grid>
        </Grid>
      </Box>
      <UpdateDisplayName
        label={modalDetails.label}
        title={modalDetails.title}
        action={modalDetails.action}
        open={modalDetails.open}
        row={modalDetails.row}
        value={modalDetails.value}
        onChange={(e) => setModalDetails((prev) => ({ ...prev, value: e }))}
        onClose={() => {
          setModalDetails(initialModalDetails);
        }}
        onSave={saveButtonHandler}
        loading={loading}
      />

      <DataGrid
        rows={sensors}
        columns={columns}
        checkboxSelection
        rowHeight={70}
        selectionModel={selectionModel}
        onSelectionModelChange={handleSelectionChange}
        disableColumnMenu
        pagination
        paginationMode="server"
        disableSelectionOnClick
        rowCount={paginationData.total}
        page={paginationData.page - 1}
        pageSize={paginationData.limit}
        rowsPerPageOptions={[1, 5, 10, 20, 50]}
        onPageChange={(newPage) => {
          paginationHandler({
            type: "pageChange",
            params: {
              newPage,
            },
          });
        }}
        onPageSizeChange={(newLimit) => {
          paginationHandler({
            type: "limitChange",
            params: {
              newLimit,
            },
          });
        }}
      />
    </Card>
  );
}
